import { updateProfile } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  Timestamp,
  where,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import UserModel, { USER_ROLES } from "../models/UserModel";

// User-related Firebase operations
export const UserService = {
  // Create or update user profile
  async saveUser(userId, userData) {
    try {
      console.log("UserService - Saving user profile for:", userId);
      if (!userId) throw new Error("User ID is required");

      const userRef = doc(db, "users", userId);
      await setDoc(userRef, userData.toFirestore(), { merge: true });

      console.log("UserService - User profile saved successfully");
      return userId;
    } catch (error) {
      console.error("UserService - Error saving user profile:", error);
      throw error;
    }
  },

  // Get user profile by ID
  async getUserById(userId) {
    try {
      console.log("UserService - Getting user profile for:", userId);
      if (!userId) throw new Error("User ID is required");

      const userRef = doc(db, "users", userId);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        console.log("UserService - User profile found");
        return UserModel.fromFirestore(userSnap);
      } else {
        console.log("UserService - User profile not found, creating default");
        // Return null if user doesn't exist
        return null;
      }
    } catch (error) {
      console.error("UserService - Error getting user profile:", error);
      throw error;
    }
  },

  // Get user by email
  async getUserByEmail(email) {
    try {
      console.log("UserService - Getting user by email:", email);
      if (!email) throw new Error("Email is required");

      const usersRef = collection(db, "users");
      const q = query(usersRef, where("email", "==", email));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        console.log("UserService - No user found with email:", email);
        return null;
      }

      // Should only be one user with this email
      console.log("UserService - User found with email:", email);
      return UserModel.fromFirestore(querySnapshot.docs[0]);
    } catch (error) {
      console.error("UserService - Error getting user by email:", error);
      throw error;
    }
  },

  // Get all users
  async getAllUsers() {
    try {
      console.log("UserService - Getting all users");
      const usersRef = collection(db, "users");

      try {
        const querySnapshot = await getDocs(usersRef);

        const users = [];
        querySnapshot.forEach((doc) => {
          users.push(UserModel.fromFirestore(doc));
        });

        console.log(`UserService - Found ${users.length} users`);
        return users;
      } catch (error) {
        // Handle permission errors explicitly
        if (
          error.code === "permission-denied" ||
          (error.message && error.message.includes("Missing or insufficient permissions"))
        ) {
          console.error(
            "UserService - Permission denied error getting users. Error details:",
            error
          );
          throw new Error(
            "Missing or insufficient permissions to access user data. You may need to sign out and sign back in to refresh your permissions."
          );
        }
        throw error;
      }
    } catch (error) {
      console.error("UserService - Error getting all users:", error);
      throw error;
    }
  },

  // Change user role
  async changeUserRole(userId, newRole) {
    try {
      console.log(`UserService - Changing role for user ${userId} to ${newRole}`);
      if (!userId) throw new Error("User ID is required");
      if (!Object.values(USER_ROLES).includes(newRole)) {
        throw new Error("Invalid role specified");
      }

      // Get the user document reference directly
      const userRef = doc(db, "users", userId);

      // Get current user data to ensure it exists
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        throw new Error("User not found");
      }

      // Log current user data
      console.log(`UserService - Current user data: ${JSON.stringify(userSnap.data())}`);

      try {
        // Update only the role and updatedAt fields
        console.log(`UserService - Attempting to update user role to ${newRole}`);
        await setDoc(
          userRef,
          {
            role: newRole,
            updatedAt: Timestamp.now(),
          },
          { merge: true }
        );
        console.log("UserService - User role updated successfully");
      } catch (updateError) {
        console.error("UserService - Error in setDoc operation:", updateError);
        if (updateError.code === "permission-denied") {
          throw new Error(
            "Permission denied: You don't have permission to change user roles. This may be due to Firestore security rules."
          );
        }
        throw updateError;
      }

      // Return updated user model
      const updatedUserSnap = await getDoc(userRef);
      return UserModel.fromFirestore(updatedUserSnap);
    } catch (error) {
      console.error("UserService - Error changing user role:", error);
      throw error;
    }
  },

  // Promote user to manager
  async promoteToManager(userId) {
    return this.changeUserRole(userId, USER_ROLES.MANAGER);
  },

  // Demote user to regular user role
  async demoteToUser(userId) {
    return this.changeUserRole(userId, USER_ROLES.USER);
  },

  // Backward compatibility methods
  async demoteToTeacher(userId) {
    console.log("UserService - demoteToTeacher called (using demoteToUser for compatibility)");
    return this.demoteToUser(userId);
  },

  async promoteToStaff(userId) {
    console.log("UserService - promoteToStaff called (using promoteToManager for compatibility)");
    return this.promoteToManager(userId);
  },

  // Initialize user if not exists
  async initializeUserIfNeeded(user, displayName = null) {
    try {
      if (!user || !user.uid || !user.email) {
        throw new Error("Valid user data is required");
      }

      // Check if user exists in Firestore
      const existingUser = await this.getUserById(user.uid);

      // Choose the most reliable display name source
      const finalDisplayName = displayName || user.displayName || "";
      console.log("UserService - Using display name:", finalDisplayName);

      if (!existingUser) {
        // Create new user
        const newUser = new UserModel({
          id: user.uid,
          email: user.email,
          displayName: finalDisplayName,
          role: USER_ROLES.USER, // Default role is now USER instead of TEACHER
          createdAt: Timestamp.now(),
        });

        await this.saveUser(user.uid, newUser);
        console.log(
          "UserService - New user initialized with user role and name:",
          finalDisplayName || "No name provided"
        );
        return newUser;
      }

      // If user exists but display name needs updating
      if (
        finalDisplayName &&
        (!existingUser.displayName || existingUser.displayName.trim() === "")
      ) {
        console.log("UserService - Updating existing user with display name:", finalDisplayName);
        existingUser.displayName = finalDisplayName;
        await this.saveUser(user.uid, existingUser);
      }

      return existingUser;
    } catch (error) {
      console.error("UserService - Error initializing user:", error);
      throw error;
    }
  },

  // Update display name in both Firebase Auth and Firestore
  async updateDisplayName(userId, newDisplayName) {
    try {
      console.log("UserService - Updating display name for:", userId);
      if (!userId) throw new Error("User ID is required");
      if (!newDisplayName.trim()) throw new Error("Display name cannot be empty");

      // First update in Firebase Auth
      const currentUser = auth.currentUser;
      if (!currentUser || currentUser.uid !== userId) {
        throw new Error("Unauthorized: Cannot update display name for another user");
      }

      await updateProfile(currentUser, { displayName: newDisplayName.trim() });

      // Then update in Firestore
      const userRef = doc(db, "users", userId);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        // Get current user data and update only the display name
        const userData = UserModel.fromFirestore(userSnap);
        userData.displayName = newDisplayName.trim();
        userData.updatedAt = Timestamp.now();

        await setDoc(userRef, userData.toFirestore(), { merge: true });
        console.log("UserService - User display name updated successfully");
        return userData;
      } else {
        // If user document doesn't exist yet, create it
        const newUserData = new UserModel({
          id: userId,
          displayName: newDisplayName.trim(),
          email: currentUser.email,
          role: USER_ROLES.USER, // Default role
          createdAt: Timestamp.now(),
        });

        await this.saveUser(userId, newUserData);
        console.log("UserService - New user created with display name");
        return newUserData;
      }
    } catch (error) {
      console.error("UserService - Error updating display name:", error);
      throw error;
    }
  },

  // Link user to staff record
  async linkUserToStaff(userId, staffId) {
    try {
      console.log(`UserService - Linking user ${userId} to staff ${staffId}`);
      if (!userId) throw new Error("User ID is required");
      if (!staffId) throw new Error("Staff ID is required");

      // Get user data
      const userData = await this.getUserById(userId);
      if (!userData) {
        throw new Error("User not found");
      }

      // Update user with staff link and role
      userData.linkedStaffId = staffId;
      userData.role = USER_ROLES.STAFF; // Automatically promote to staff role

      await this.saveUser(userId, userData);
      console.log(`UserService - User ${userId} linked to staff ${staffId} successfully`);

      return userData;
    } catch (error) {
      console.error("UserService - Error linking user to staff:", error);
      throw error;
    }
  },

  // Unlink user from staff record
  async unlinkUserFromStaff(userId) {
    try {
      console.log(`UserService - Unlinking user ${userId} from staff`);
      if (!userId) throw new Error("User ID is required");

      // Get user data
      const userData = await this.getUserById(userId);
      if (!userData) {
        throw new Error("User not found");
      }

      // Only proceed if user is linked to a staff
      if (!userData.linkedStaffId) {
        console.log("UserService - User is not linked to any staff record");
        return userData;
      }

      // Update user to remove staff link
      // Don't change the role - must be done explicitly
      userData.linkedStaffId = null;

      await this.saveUser(userId, userData);
      console.log(`UserService - User ${userId} unlinked from staff successfully`);

      return userData;
    } catch (error) {
      console.error("UserService - Error unlinking user from staff:", error);
      throw error;
    }
  },

  // Create a student enrollment in a class
  async enrollInClass(userId, classId) {
    try {
      console.log(`UserService - Enrolling user ${userId} in class ${classId}`);
      if (!userId) throw new Error("User ID is required");
      if (!classId) throw new Error("Class ID is required");

      // Get user data
      const userData = await this.getUserById(userId);
      if (!userData) {
        throw new Error("User not found");
      }

      // Add class to enrolled classes if not already enrolled
      if (!userData.enrolledClasses) {
        userData.enrolledClasses = [];
      }

      if (!userData.enrolledClasses.includes(classId)) {
        userData.enrolledClasses.push(classId);
        await this.saveUser(userId, userData);
        console.log(`UserService - User ${userId} enrolled in class ${classId} successfully`);
      } else {
        console.log(`UserService - User ${userId} is already enrolled in class ${classId}`);
      }

      return userData;
    } catch (error) {
      console.error("UserService - Error enrolling user in class:", error);
      throw error;
    }
  },

  // Remove a student enrollment from a class
  async unenrollFromClass(userId, classId) {
    try {
      console.log(`UserService - Unenrolling user ${userId} from class ${classId}`);
      if (!userId) throw new Error("User ID is required");
      if (!classId) throw new Error("Class ID is required");

      // Get user data
      const userData = await this.getUserById(userId);
      if (!userData) {
        throw new Error("User not found");
      }

      // Remove class from enrolled classes if enrolled
      if (userData.enrolledClasses && userData.enrolledClasses.includes(classId)) {
        userData.enrolledClasses = userData.enrolledClasses.filter((id) => id !== classId);
        await this.saveUser(userId, userData);
        console.log(`UserService - User ${userId} unenrolled from class ${classId} successfully`);
      } else {
        console.log(`UserService - User ${userId} is not enrolled in class ${classId}`);
      }

      return userData;
    } catch (error) {
      console.error("UserService - Error unenrolling user from class:", error);
      throw error;
    }
  },
};

export default UserService;
